import { Stack, Text, IconButton, getTheme, IIconProps, Pivot, SharedColors, IStyleFunctionOrObject, IPivotStyleProps, IPivotStyles, PivotItem, IRenderFunction, IPivotItemProps, DirectionalHint, TooltipHost, Icon } from '@fluentui/react';
import { Dispatch, useState } from 'react';
import { useSelector } from 'react-redux';
import { getStageInspectionModules } from '../../../../../../../core/actions/configurator-actions';
import { IStore } from '../../../../../../../core/store';
import { getLocalizedInspectionModeName, getLocalizedInspectionName } from '../../../../../../../localization/helpers/transaction';
import { strings } from '../../../../../../../localization/strings';
import { ModeConfiguratorCommonPivot } from './pivots/ModeConfiguratorCommonPivot';
import { IEraseConfig, Inspections, ProcessStages } from "@piceasoft/core";
import { CSHHelpLink } from '../../../../../help/CSHHelp';

type TProps = {
    index: number
    stage: ProcessStages
    onDissmiss: () => void
    onChangeDispatch: Dispatch<any>
}

type TEraseModeConfiguratorPivotState = "common"

export const EraseConfigurator: React.FC<TProps> = ({ index, onDissmiss, onChangeDispatch, stage }) => {
    const config = useSelector((s: IStore) => getStageInspectionModules(stage, s)?.find(m => m.index === index)?.config as IEraseConfig)
    const [pivotState, setPivotState] = useState<TEraseModeConfiguratorPivotState>("common")

    const getTabId = (itemKey: string) => {
        return `EraseModeConfiguratorPivot_${itemKey}`;
    };

    const handlePivotLinkClick = (item?: PivotItem) => {
        setPivotState(item?.props.itemKey! as TEraseModeConfiguratorPivotState);
    };

    const getPivotHeaderText = (m: string): string => {
        switch (m) {
            case "common": return strings.CONSTRUCTOR.INSPECTIONS.ERASE.MODE_CONFIGURATOR.PIVOTS.COMMON.TITLE
            default: return m;
        }
    };

    const onRenderItemLink: IRenderFunction<IPivotItemProps> = (iProps) => {
        return (
            <span style={{ ...iProps?.style }}>
                <TooltipHost directionalHint={DirectionalHint.bottomCenter} content={iProps?.headerText}>
                    <Icon
                        iconName={iProps?.itemIcon}
                        style={{ fontSize: 16, display: "flex", padding: '0px 12px 2px 12px' }} />
                </TooltipHost>
            </span>
        )
    };

    return (
        <Stack verticalFill grow>
            <Stack horizontal grow style={{ padding: 8 }}>
                <Stack style={{ padding: 8 }} grow tokens={{ childrenGap: 4 }}>
                    <Text variant="xLarge">
                        {config?.ui?.title ?? getLocalizedInspectionName(Inspections.Erase)}
                    </Text>
                    <Text variant="small">
                        {getLocalizedInspectionModeName(Inspections.Erase, config as IEraseConfig)}
                    </Text>
                </Stack>
                <Stack horizontal style={{ alignItems: "center" }}>
                        <CSHHelpLink articleid="1138"/>
                        <IconButton onClick={onDissmiss} styles={iconButtonStyles} iconProps={cancelIcon} ariaLabel="Close" />
                    </Stack>
            </Stack>
            <Stack.Item tokens={{ padding: "0px 16px 0px 16px" }} style={{ borderBottom: `1px solid ${theme.semanticColors.bodyDivider}` }}>
                <Pivot styles={pivotStyles} selectedKey={pivotState} onLinkClick={handlePivotLinkClick} headersOnly={true} getTabId={getTabId} overflowBehavior="none">
                    <PivotItem
                        itemIcon={'Settings'}
                        headerText={getPivotHeaderText("common")}
                        onRenderItemLink={onRenderItemLink}
                        itemKey="common"
                    />
                </Pivot>
            </Stack.Item>
            <Stack.Item verticalFill style={{ position: 'relative' }}>
                <PivotStateBase
                    index={index}
                    pivotState={pivotState}
                    stage={stage}
                    erase={config}
                    onChangeDispatch={onChangeDispatch}
                />
            </Stack.Item>
        </Stack>
    )
}

type TPivotStateBaseProps = {
    index: number,
    erase: IEraseConfig,
    stage: ProcessStages
    pivotState: TEraseModeConfiguratorPivotState,
    onChangeDispatch: Dispatch<any>
}

const PivotStateBase = (props: TPivotStateBaseProps) => {
    let {index, erase, pivotState, onChangeDispatch} = props
    switch (pivotState) {
        case "common":
            return (
                <ModeConfiguratorCommonPivot
                    index={index}
                    erase={erase}
                    stage={props.stage}
                    onChangeDispatch={onChangeDispatch}
                />
            )
        default: return <></>
    }
}

const cancelIcon: IIconProps = { iconName: 'Cancel' };

let theme = getTheme()

const iconButtonStyles = {
    root: {
        color: theme.palette.neutralPrimary,
        marginLeft: 'auto',
        marginTop: '4px',
        marginRight: '2px',
    },
    rootHovered: {
        color: theme.palette.neutralDark,
    },
};

const pivotStyles: IStyleFunctionOrObject<IPivotStyleProps, IPivotStyles> = {
    linkIsSelected: {
        '::before': {
            display: 'none'
        },
        '::after': {
            display: 'none'
        },
        backgroundColor: theme.palette.white,
        fontWeight: 400,
        color: SharedColors.cyanBlue10
    },
    linkContent: {
        fontSize: 12,
        margin: 0,
        padding: '0px 12px 2px 12px',
        display: "flex"
    },
    link: {
        borderTop: `1px solid ${theme.palette.neutralLight}`,
        borderLeft: `1px solid ${theme.palette.neutralLight}`,
        padding: 0,
        margin: 0,
        height: '34px',
        lineHeight: '34px',
        '::before': {
            display: 'none'
        },
        '::after': {
            display: 'none'
        },
        color: SharedColors.gray20
    },
    root: {
        display: "flex",
        zIndex: 20,
        marginRight: 16,
        'button:last-child': {
            borderRight: `1px solid ${theme.palette.neutralLight}`
        }
    }
}
